import { useBusinessServiceRequestWithFirstQueryLoading } from '@ingenico/react-section-utils';
import { isArray, toString } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { DeviceMetrics } from '../domain/models/deviceMetrics.model';
import type { ADeviceRequest } from './useProcessDeviceRequest';
import { useProcessRequest } from './useProcessDeviceRequest';

export interface UseLogicalDeviceConfigurationsParams {
  readonly logicalDeviceInfo: { readonly id?: string };
  readonly shouldFetch?: boolean;
}

export interface UseLogicalDeviceConfigurationsReturn {
  readonly metricsProcessed?: ADeviceRequest<DeviceMetrics | DeviceMetrics[]>;
}

const qttAllowedMultipleLogicalDeviceConfig = 2;

function checkShouldEnableQueries(
  data: UseLogicalDeviceConfigurationsParams | UseLogicalDeviceConfigurationsParams[],
): boolean {
  if (Array.isArray(data)) {
    return (
      data.length > 0 &&
      data.length <= qttAllowedMultipleLogicalDeviceConfig &&
      data.every((item) => !!item.logicalDeviceInfo?.id && item.shouldFetch === true)
    );
  }
  return !!data.logicalDeviceInfo?.id && data.shouldFetch === true;
}

function getLogicalDeviceParams(
  data: UseLogicalDeviceConfigurationsParams | UseLogicalDeviceConfigurationsParams[],
): { readonly deviceId: string }[] | { readonly deviceId: string } {
  return Array.isArray(data)
    ? data.map(({ logicalDeviceInfo }) => ({
        deviceId: toString(logicalDeviceInfo?.id),
      }))
    : { deviceId: toString(data.logicalDeviceInfo?.id) };
}

function useSingleLogicalDeviceConfigurations<
  T extends
    | UseLogicalDeviceConfigurationsParams
    | UseLogicalDeviceConfigurationsParams[] = UseLogicalDeviceConfigurationsParams,
>(params: T): UseLogicalDeviceConfigurationsReturn | null {
  const isParamArray = useMemo(() => isArray(params), [params]);

  const shouldEnableQueries = useMemo<boolean>(
    () => !isParamArray && checkShouldEnableQueries(params),
    [isParamArray, params],
  );

  const logicalDeviceParams = useMemo(() => getLogicalDeviceParams(params), [params]);

  /* -------------------------------------------------------------------------- */
  /*                                QUERIES START                               */
  /* -------------------------------------------------------------------------- */

  const { isFirstLoading: isSingleFirstLoadingMetrics, ...singleDeviceMetricsQuery } =
    useBusinessServiceRequestWithFirstQueryLoading(
      DeviceManagementService,
      useCallback(
        (deviceManagementService) =>
          shouldEnableQueries && !Array.isArray(logicalDeviceParams)
            ? deviceManagementService.getDeviceMetrics(logicalDeviceParams)
            : null,
        [logicalDeviceParams, shouldEnableQueries],
      ),
    );

  /* -------------------------------------------------------------------------- */
  /*                                 QUERIES END                                */
  /* -------------------------------------------------------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                         COMPUTE NEW PROPERTY START                         */
  /* -------------------------------------------------------------------------- */

  const metricsProcessed = useProcessRequest({
    requestQuery: singleDeviceMetricsQuery,
    isFirstLoading: isSingleFirstLoadingMetrics,
  });
  /* -------------------------------------------------------------------------- */
  /*                          COMPUTE NEW PROPERTY END                          */
  /* -------------------------------------------------------------------------- */

  return isArray(params)
    ? null
    : {
        metricsProcessed,
      };
}

function useMultipleLogicalDeviceConfigurations<
  T extends
    | UseLogicalDeviceConfigurationsParams
    | UseLogicalDeviceConfigurationsParams[] = UseLogicalDeviceConfigurationsParams,
>(params: T): UseLogicalDeviceConfigurationsReturn | null {
  const isParamArray = useMemo(() => isArray(params), [params]);

  const shouldEnableQueries = useMemo<boolean>(
    () => checkShouldEnableQueries(params) && isParamArray,
    [isParamArray, params],
  );

  const logicalDeviceParams = useMemo(() => getLogicalDeviceParams(params), [params]);

  /* -------------------------------------------------------------------------- */
  /*                                 QUERY START                                */
  /* -------------------------------------------------------------------------- */
  const { isFirstLoading: isMultipleFirstLoadingMetrics, ...multipleDeviceMetricsQuery } =
    useBusinessServiceRequestWithFirstQueryLoading(
      DeviceManagementService,
      useCallback(
        (deviceManagementService) =>
          shouldEnableQueries && Array.isArray(logicalDeviceParams) && logicalDeviceParams.length > 0
            ? deviceManagementService.getMultipleDeviceMetrics(logicalDeviceParams)
            : null,
        [logicalDeviceParams, shouldEnableQueries],
      ),
    );

  /* -------------------------------------------------------------------------- */
  /*                                  QUERY END                                 */
  /* -------------------------------------------------------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                         COMPUTE NEW PROPERTY START                         */
  /* -------------------------------------------------------------------------- */

  const metricsProcessed = useProcessRequest({
    requestQuery: multipleDeviceMetricsQuery,
    isFirstLoading: isMultipleFirstLoadingMetrics,
  });
  /* -------------------------------------------------------------------------- */
  /*                          COMPUTE NEW PROPERTY END                          */
  /* -------------------------------------------------------------------------- */

  return isParamArray
    ? {
        metricsProcessed,
      }
    : null;
}

export function useLogicalDeviceConfigurations<
  T extends
    | UseLogicalDeviceConfigurationsParams
    | UseLogicalDeviceConfigurationsParams[] = UseLogicalDeviceConfigurationsParams,
>(params: T): UseLogicalDeviceConfigurationsReturn {
  const useSingle = useSingleLogicalDeviceConfigurations(params);
  const useMultiple = useMultipleLogicalDeviceConfigurations(params);

  return (useSingle ?? useMultiple)!;
}
